import { Mask } from 'maska';
import React, { forwardRef, useEffect, useMemo, useRef, useState, } from 'react';
import styled, { css } from 'styled-components';
import chroma from 'chroma-js';
import { mergeRefs } from '../../helpers';
import { Icon } from '../Icon';
export const InputWrapper = styled.label `
  color: var(--input-color, ${({ theme }) => theme.style.inputDefaultTextColor});
  background-color: var(--input-background, ${({ theme }) => theme.style.inputDefaultBackgroundColor});
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  border-radius: ${({ theme }) => theme.style.inputBorderRadius};
  padding: ${({ theme }) => theme.style.inputPadding};
  font-weight: ${({ theme }) => theme.style.inputFontWeight};
  font-size: ${({ theme }) => theme.style.inputFontSize};
  cursor: text;
  box-sizing: border-box;
  overflow: clip;
  transition:
    background-color 0.2s,
    box-shadow 0.2s,
    color 0.2s,
    opacity 0.2s,
    border-radius 0.2s;
  box-shadow:
    inset 0 0 0 var(--input-border, 1px)
      var(--input-border-color, ${({ theme }) => theme.style.inputDefaultBorderColor}),
    0 0 0 var(--input-outline, 0px) var(--input-outline-color, transparent),
    ${({ theme }) => theme.style.depressedShadow};

  &:before {
    content: ${({ $hasError }) => ($hasError && typeof $hasError === 'string' ? 'attr(data-error)' : '')};
    position: absolute;
    top: calc(${({ theme }) => theme.style.inputFocussedOutline} * -1);
    left: calc(${({ theme }) => theme.style.inputFocussedOutline} * -1);
    right: calc(${({ theme }) => theme.style.inputFocussedOutline} * -1);
    background-color: ${({ theme }) => theme.style.inputErrorOutlineColor};
    color: ${({ theme }) => theme.style.inputErrorTextColor};
    padding-left: calc(16px + ${({ theme }) => theme.style.inputFocussedOutline});
    padding-right: calc(16px + ${({ theme }) => theme.style.inputFocussedOutline});
    z-index: -1;
    padding-top: ${({ $isFocussed, $hasError }) => ($isFocussed && typeof $hasError === 'string' ? '48px' : '23px')};
    opacity: ${({ $isFocussed, $hasError }) => ($isFocussed && typeof $hasError === 'string' ? 1 : 0)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: table-cell;
    font-size: 12px;
    font-weight: 500;
    line-height: var(--input-line-height);
    border-radius: calc(
      ${({ theme }) => theme.style.inputBorderRadius} + ${({ theme }) => theme.style.inputFocussedOutline}
    );
    padding-bottom: 7px;
    display: block;
    transition:
      padding-top 0.2s,
      opacity 0.2s;

    ${({ $size }) => $size === 'small' &&
    css `
        padding-top: ${({ $isFocussed, $hasError }) => ($isFocussed && $hasError ? '40px' : '23px')};
        padding-left: calc(12px + ${({ theme }) => theme.style.inputFocussedOutline});
        padding-right: calc(12px + ${({ theme }) => theme.style.inputFocussedOutline});
        font-size: 11px;
        font-weight: 500;
        line-height: 12px;
      `}

    ${({ $size }) => $size === 'large' &&
    css `
        padding-top: ${({ $isFocussed, $hasError }) => ($isFocussed && $hasError ? '52px' : '23px')};
      `}
  }

  --input-placeholder-color: ${({ theme }) => theme.style.inputDefaultPlaceholderTextColor};

  --input-background: ${({ theme, $isHovered }) => $isHovered && theme.style.inputDefaultHoveredBackgroundColor};
  --input-border-color: ${({ theme, $isHovered }) => $isHovered && theme.style.inputDefaultHoveredBorderColor};
  --input-color: ${({ theme, $isHovered }) => $isHovered && theme.style.inputDefaultTextColor};
  --input-placeholder-color: ${({ theme, $isHovered, $isFocussed }) => ($isHovered || $isFocussed) && theme.style.inputDefaultHoveredPlaceholderTextColor};

  --input-background: ${({ $isFocussed, theme }) => $isFocussed && theme.style.inputFocussedBackgroundColor};
  --input-border: ${({ $isFocussed, theme }) => $isFocussed && theme.style.inputFocussedBorder};
  --input-border-color: ${({ theme, $isFocussed }) => $isFocussed && theme.style.inputFocussedBorderColor};
  --input-outline: ${({ $isFocussed, theme }) => $isFocussed && theme.style.inputFocussedOutline};
  --input-outline-color: ${({ theme, $isFocussed }) => $isFocussed && theme.style.inputFocussedOutlineColor};

  cursor: ${({ $isDisabled }) => $isDisabled && 'not-allowed'};
  opacity: ${({ $isDisabled }) => $isDisabled && 0.75};

  --input-color: ${({ theme, $hasError }) => $hasError && theme.style.inputErrorTextColor};
  --input-border-color: ${({ theme, $hasError }) => $hasError && theme.style.inputErrorBorderColor};
  --input-background: ${({ $hasError, theme }) => $hasError && theme.style.inputErrorBackgroundColor};
  --input-outline-color: ${({ theme, $hasError }) => $hasError && theme.style.inputErrorOutlineColor};
  --input-placeholder-color: ${({ theme, $hasError }) => $hasError && theme.style.inputErrorPlaceholderTextColor};
  --input-placeholder-color: ${({ theme, $isHovered, $isFocussed, $hasError }) => ($isHovered || $isFocussed) && $hasError && theme.style.inputErrorHoveredPlaceholderTextColor};

  --input-line-height: ${({ theme }) => theme.style.inputLineHeight};

  ${({ $size }) => $size === 'small' &&
    css `
      border-radius: ${({ theme }) => theme.style.inputSmallBorderRadius};
      padding: ${({ theme }) => theme.style.inputSmallPadding};
      font-weight: ${({ theme }) => theme.style.inputSmallFontWeight};
      font-size: ${({ theme }) => theme.style.inputSmallFontSize};

      --input-line-height: ${({ theme }) => theme.style.inputSmallLineHeight};
    `}

  ${({ $size }) => $size === 'large' &&
    css `
      border-radius: ${({ theme }) => theme.style.inputLargeBorderRadius};
      padding: ${({ theme }) => theme.style.inputLargePadding};
      font-weight: ${({ theme }) => theme.style.inputLargeFontWeight};
      font-size: ${({ theme }) => theme.style.inputLargeFontSize};

      --input-line-height: ${({ theme }) => theme.style.inputLargeLineHeight};
    `}

  ${({ $variant }) => $variant === 'light' &&
    css `
      --input-shadow: ${({ theme, $isFocussed }) => $isFocussed && theme.primary.blendToBackground(1000, 100)};

      --input-background: ${({ theme, $hasError }) => !$hasError && theme.background};
      --input-border-color: ${({ theme, $isFocussed, $hasError }) => $hasError && !$isFocussed && theme.danger.blendToBackground(1000, 150)};

      --input-shadow: ${({ theme, $hasError }) => $hasError && theme.danger.blendToBackground(1000, 100)};
      --input-shadow-second: ${({ $hasError, $isFocussed }) => ($hasError || $isFocussed) && css `transparent`};
    `}

  ${({ $variant }) => $variant === 'subtle' &&
    css `
      --input-background: ${({ theme }) => theme.background};
      --input-border-color: ${({ theme }) => theme.secondary.blendToBackground(200)};

      box-shadow:
        inset 0 0 0 var(--input-border, 1px)
          var(--input-border-color, ${({ theme }) => theme.secondary.blendToBackground(1100, 150)}),
        0 0 0 var(--input-outline, 0px) var(--input-outline-color, transparent);

      transition:
        background 0.2s,
        box-shadow 0.2s;

      --input-background: ${({ theme, $isHovered }) => $isHovered && theme.style.inputDefaultHoveredBackgroundColor};
      --input-border-color: ${({ theme, $isHovered }) => $isHovered && theme.style.inputDefaultHoveredBorderColor};
    `}
`;
export const InputLoadingWrapper = styled.div `
  position: absolute;
  inset: 1px;
  z-index: 1;
  display: flex;
  cursor: progress;
  justify-content: center;
  align-items: center;
  border-radius: inherit;
  background-color: ${({ theme }) => chroma(theme.style.inputDefaultBackgroundColor ?? theme.background)
    .alpha(0.025)
    .css()};
  backdrop-filter: blur(2px);
  transition: opacity 0.2s;

  ${({ $isLoading }) => $isLoading
    ? css `
          opacity: 1;
        `
    : css `
          opacity: 0;
        `}
`;
export const InputLoading = styled(Icon.Loading) `
  --icon-color: currentColor;
  ${({ $isLoading }) => $isLoading
    ? css `
          animation: show 0.2s ease forwards;
        `
    : css `
          animation: hide 0.2s ease forwards;
        `}

  transform: translateY(-8px) scale(0.75);

  @keyframes show {
    100% {
      transform: none;
    }
  }

  @keyframes hide {
    0% {
      transform: none;
    }

    100% {
      transform: translateY(-8px) scale(0.75);
    }
  }
`;
export const InputContainer = styled.div `
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  ${({ $tabularFigures }) => $tabularFigures &&
    css `
      font-feature-settings: 'tnum';
    `}
`;
const InputElementSuffix = ({ children, $inputDirection, placeholder, $textAlign, value, }) => {
    return (React.createElement(InputElementSuffixWrapper, { "$inputDirection": $inputDirection, "$textAlign": $textAlign },
        React.createElement(InputElementSuffixHiddenPart, null, !!value ? value : placeholder),
        children));
};
const InputElementSuffixWrapper = styled.div `
  display: flex;
  position: absolute;
  top: 0;
  pointer-events: none;
  letter-spacing: ${({ theme }) => theme.style.inputLetterSpacing};
  line-height: var(--input-line-height);
  height: var(--input-line-height);
  width: 100%;
  max-width: 100%;
  align-items: center;

  ${({ $inputDirection, $textAlign }) => {
    if ((!$inputDirection || $inputDirection === 'ltr') && (!$textAlign || $textAlign === 'left')) {
        return css `
        left: 0;
        flex-direction: row;
      `;
    }
    if ($inputDirection === 'ltr' && $textAlign === 'right') {
        return css `
        right: 0;
        flex-direction: row;
        justify-content: end;
      `;
    }
    if ($inputDirection === 'rtl' && $textAlign === 'left') {
        return css `
        left: 0;
        flex-direction: row-reverse;
        justify-content: start;
      `;
    }
    return css `
      right: 0;
      flex-direction: row-reverse;
    `;
}};
`;
const InputElementSuffixHiddenPart = styled.span `
  opacity: 0;
  overflow: hidden;
`;
const PersistentPlaceholderText = styled.span `
  color: ${({ theme }) => theme.style.inputDefaultPlaceholderTextColor};
  letter-spacing: ${({ theme }) => theme.style.inputLetterSpacing};
  line-height: var(--input-line-height);
  height: var(--input-line-height);
`;
const PersistentPlaceholder = React.memo(({ $inputDirection, placeholder, $textAlign, value }) => {
    if (!placeholder || !value) {
        return null;
    }
    const valueLength = value ? value.length : 0;
    const placeholderLength = placeholder ? placeholder.length : 0;
    const rightAlign = $inputDirection ? $inputDirection === 'rtl' : $textAlign === 'right';
    const visiblePlaceholder = rightAlign
        ? placeholder.slice(0, Math.max(0, placeholderLength - valueLength))
        : placeholder.slice(Math.min(valueLength, placeholderLength));
    return React.createElement(PersistentPlaceholderText, null, visiblePlaceholder);
});
const ValueSuffix = styled.span `
  ${({ $inputDirection, $textAlign }) => {
    if ((!$inputDirection || $inputDirection === 'ltr') && (!$textAlign || $textAlign === 'left')) {
        return css `
        padding-left: 4px;
      `;
    }
    if ($inputDirection === 'ltr' && $textAlign === 'right') {
        return css `
        padding-left: 4px;
      `;
    }
    if ($inputDirection === 'rtl' && $textAlign === 'left') {
        return css `
        padding-right: 4px;
      `;
    }
    return css `
      padding-right: 4px;
    `;
}}
`;
export const ValuePrefixWrapper = styled.div `
  margin: -2px;
`;
export const IconWrapper = styled.div `
  margin: -2px;
  transform: translateY(-0.5px);

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(600)};
    --icon-size: 16px;

    ${({ $size }) => $size === 'small' &&
    css `
        margin: 0 -4px 0 0;
      `}

    path {
      transition:
        stroke 0.2s,
        fill 0.2s;
    }

    ${({ $isFocussed }) => $isFocussed &&
    css `
        --icon-color: ${({ theme }) => theme.style.inputFocussedBorderColor};
      `}
  }
`;
export const InputElement = styled.input `
  appearance: none;
  background: none;
  border: none;
  outline: none;
  overflow: ${({ $isDynamicWidth }) => $isDynamicWidth && 'hidden'};
  padding: 0;
  margin: 0;
  color: inherit;
  width: 100%;
  max-width: 100%;
  letter-spacing: ${({ theme }) => theme.style.inputLetterSpacing};
  line-height: var(--input-line-height);
  max-height: var(--input-line-height);
  height: var(--input-line-height);
  display: block;
  caret-color: ${({ theme }) => theme.primary.background};
  transition: opacity 0.2s;
  font-family: ${({ $isMonospaced }) => $isMonospaced &&
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'};
  font-size: var(--input-font-size);

  &:-webkit-autofill,
  &:-webkit-autofill:focus {
    transition:
      background-color 0s 9999s,
      color 0s 9999s;
  }

  [data-autocompleted] {
    background-color: transparent !important;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &::placeholder {
    color: var(--input-placeholder-color);
    transition: color 0.2s;

    ${({ $isMonospaced }) => $isMonospaced &&
    `
      font-family: "Inter", sans-serif;
    `}
  }

  ${({ $isLoading }) => $isLoading &&
    css `
      opacity: 0;
      pointer-events: none;
    `}

  text-align: ${({ $inputDirection, $textAlign }) => $inputDirection ? ($inputDirection === 'ltr' ? 'left' : 'right') : $textAlign};
`;
export const Input = forwardRef((props, ref) => {
    const [isLoadingState, setIsLoadingState] = useState(props.isLoading ?? false);
    const [isHoveredState, setIsHoveredState] = useState(props.isHovered ?? false);
    const [isFocussedState, setIsFocussedState] = useState(props.isFocussed ?? false);
    const [isFilledState, setIsFilledState] = useState(props.isFilled ?? false);
    const onChange = props.onChange ??
        (() => {
            /* NOOP */
        });
    const onFocus = props.onFocus ??
        (() => {
            /* NOOP */
        });
    const onBlur = props.onBlur ??
        (() => {
            /* NOOP */
        });
    const onKeyDown = props.onKeyDown ??
        (() => {
            /* NOOP */
        });
    const inputRef = useRef(null);
    const valueSuffixRef = useRef(null);
    const mask = useMemo(() => new Mask({ mask: props.mask, reversed: props.textAlign === 'right' }), [props.mask, props.textAlign]);
    const styleProps = {
        $isHovered: props.isHovered ?? isHoveredState,
        $isFocussed: props.isFocussed ?? isFocussedState,
        $isFilled: props.isFilled ?? isFilledState,
        $isDisabled: props.isDisabled ?? false,
        $isLoading: props.isLoading ?? isLoadingState,
        $isPressed: props.isPressed ?? false,
        $hasError: props.hasError ?? false,
        $variant: props.variant ?? 'default',
        $size: props.size ?? 'default',
        $isDynamicWidth: !!props.valueSuffix,
    };
    const handleClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (props.isDisabled) {
            return;
        }
        if (props.onClick) {
            props.onClick();
        }
        setIsFocussedState(true);
    };
    const handleHoverChange = (v) => {
        if (props.isDisabled || props.isLoading) {
            return;
        }
        if (props.onHoverChange) {
            props.onHoverChange(v);
        }
        setIsHoveredState(v);
    };
    const handleFocusChange = (v) => {
        if (props.isDisabled || props.isLoading) {
            return;
        }
        if (props.onFocusChange) {
            props.onFocusChange(v);
        }
        setIsFocussedState(v);
    };
    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && props.autoBlur) {
            inputRef.current?.blur();
        }
    };
    let pattern = props.pattern;
    if (props.type === 'email') {
        pattern = '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
    }
    useEffect(() => {
        if (props.isFocussed ?? isFocussedState) {
            inputRef.current?.focus();
        }
        else {
            inputRef.current?.blur();
        }
    }, [isFocussedState, props.isFocussed]);
    useEffect(() => {
        if (props.value) {
            setIsFilledState(props.value.length > 0);
        }
    }, [props.value, inputRef]);
    useEffect(() => {
        if (props.autoFocus) {
            setIsFocussedState(true);
        }
    }, [props.autoFocus]);
    useEffect(() => {
        if (props.isLoading) {
            setIsLoadingState(true);
        }
        else {
            setTimeout(() => {
                setIsLoadingState(false);
            }, 200);
        }
    }, [props.isLoading]);
    const maskProps = !!props.mask
        ? {
            value: mask.masked(props.value ?? ''),
            onChange: (e) => onChange(mask.unmasked(e.target.value)),
            placeholder: mask.masked(props.placeholder ?? ''),
        }
        : {
            onChange: (e) => onChange(e.target.value),
            value: props.value ?? '',
            placeholder: props.placeholder,
        };
    const inputDirection = useMemo(() => (props.persistentPlaceholder ? props.inputDirection : undefined), [props.persistentPlaceholder, props.inputDirection]);
    const [valueSuffixWidth, setValueSuffixWidth] = useState(valueSuffixRef.current?.offsetWidth ?? 0);
    useEffect(() => {
        const observer = new ResizeObserver(() => {
            setValueSuffixWidth(valueSuffixRef.current?.offsetWidth ?? 0);
        });
        if (valueSuffixRef.current) {
            observer.observe(valueSuffixRef.current);
        }
        return () => {
            if (valueSuffixRef.current) {
                observer.unobserve(valueSuffixRef.current);
            }
        };
    }, []);
    const { paddingLeft, paddingRight } = useMemo(() => {
        if (props.persistentPlaceholder) {
            if (props.inputDirection === 'rtl' && props.textAlign === 'left') {
                return {
                    paddingRight: `calc(100% - 3px - ${props.placeholder?.length ?? 0}ch - ${valueSuffixWidth}px)`,
                };
            }
            if (props.inputDirection === 'ltr' && props.textAlign === 'right') {
                return {
                    paddingLeft: `calc(100% - 3px - ${props.placeholder?.length ?? 0}ch - ${valueSuffixWidth}px)`,
                };
            }
        }
        if (props.valueSuffix) {
            if (props.textAlign === 'right') {
                return {
                    paddingLeft: `${valueSuffixWidth}px`,
                };
            }
            if (props.textAlign === 'left') {
                return {
                    paddingRight: `${valueSuffixWidth}px`,
                };
            }
        }
        return {};
    }, [
        props.inputDirection,
        props.textAlign,
        props.persistentPlaceholder,
        props.placeholder,
        props.valueSuffix,
        valueSuffixWidth,
    ]);
    return (React.createElement(InputWrapper, { style: props.style, className: props.className, onMouseEnter: () => handleHoverChange(true), onMouseMove: () => handleHoverChange(true), onMouseLeave: () => handleHoverChange(false), onClick: handleClick, "data-error": typeof props.hasError === 'string' ? props.hasError : undefined, ...styleProps },
        props.icon && React.createElement(IconWrapper, { ...styleProps }, props.icon),
        props.valuePrefix && React.createElement(ValuePrefixWrapper, null, props.valuePrefix),
        React.createElement(InputContainer, { ...styleProps, "$tabularFigures": !!props.persistentPlaceholder && !!props.placeholder, "$textAlign": props.textAlign },
            React.createElement(InputElement, { autoComplete: props.autoComplete, onKeyPress: handleKeyPress, autoFocus: props.autoFocus, "data-1p-ignore": props.autoComplete === 'off' ? true : undefined, "$textAlign": props.textAlign, disabled: props.isDisabled ?? false, type: props.type ?? 'text', pattern: pattern, "$isLoading": styleProps.$isLoading, readOnly: props.readOnly ?? false, maxLength: props.maxLength, ...maskProps, "$inputDirection": inputDirection, placeholder: maskProps.placeholder, onBlur: (e) => {
                    handleFocusChange(false);
                    onBlur(e);
                }, onFocus: (e) => {
                    handleFocusChange(true);
                    onFocus(e);
                }, onKeyDown: (e) => {
                    onKeyDown(e);
                }, "$isDynamicWidth": styleProps.$isDynamicWidth, ref: mergeRefs([ref, inputRef]), style: { paddingLeft, paddingRight }, "$isMonospaced": props.isMonospaced }),
            React.createElement(InputElementSuffix, { "$inputDirection": inputDirection, placeholder: props.placeholder, "$textAlign": props.textAlign, value: maskProps.value },
                props.persistentPlaceholder && props.placeholder && (React.createElement(PersistentPlaceholder, { "$inputDirection": inputDirection, placeholder: maskProps.placeholder, "$textAlign": props.textAlign, value: maskProps.value })),
                props.valueSuffix && (React.createElement(ValueSuffix, { ref: valueSuffixRef, "$inputDirection": inputDirection, "$textAlign": props.textAlign }, props.valueSuffix)))),
        props.children,
        styleProps.$isLoading && (React.createElement(InputLoadingWrapper, { "$isLoading": styleProps.$isLoading },
            React.createElement(InputLoading, { "$isLoading": styleProps.$isLoading })))));
});
//# sourceMappingURL=Input.js.map